import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, downloadText, exportEntries, getCostCodes, getCustomers, getEntries, getEntryCount, patchMe, updateEntry, type Entry } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { getLastNDaysRange, toISODate } from "@/lib/time";
import { useDashboardRoles } from "@/pages/dashboard/useDashboardRoles";

export function useRecentActivityViewModel() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { isAdmin, canEditPunches, managedEditUserIds } = useDashboardRoles();
  // Filter searchable states for recent activity
  const [recentCostCodeFilterSearch, setRecentCostCodeFilterSearch] = React.useState("");
  const [recentCostCodeFilterOpen, setRecentCostCodeFilterOpen] = React.useState(false);
  const recentCostCodeFilterRef = React.useRef<HTMLDivElement>(null);
  const [recentCustomerFilterSearch, setRecentCustomerFilterSearch] = React.useState("");
  const [recentCustomerFilterOpen, setRecentCustomerFilterOpen] = React.useState(false);
  const recentCustomerFilterRef = React.useRef<HTMLDivElement>(null);
  const [recentPage, setRecentPage] = React.useState(0);
  const [recentPageSize, setRecentPageSize] = React.useState(6);
  const [recentFilterCostCode, setRecentFilterCostCode] = React.useState("all");
  const [recentFilterCustomer, setRecentFilterCustomer] = React.useState("all");
  const [recentFromDate, setRecentFromDate] = React.useState(() => {
    const { from } = getLastNDaysRange(7);
    return from;
  });
  const [recentToDate, setRecentToDate] = React.useState(() => {
    const { to } = getLastNDaysRange(7);
    return to;
  });
  const [recentSearch, setRecentSearch] = React.useState("");
  const [recentCompact, setRecentCompact] = React.useState<boolean>(() => {
    // Prefer the server value at mount time if the auth user is already available.
    // The effect further down keeps this in sync on user changes / login.
    return typeof user?.recent_compact === "boolean" ? user.recent_compact : true;
  });
  const [fieldNotesDialogOpen, setFieldNotesDialogOpen] = React.useState(false);
  const [fieldNotesEntry, setFieldNotesEntry] = React.useState<Entry | null>(null);
  const [fieldNotesText, setFieldNotesText] = React.useState("");

  const [exportError, setExportError] = React.useState<string | null>(null);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (recentCustomerFilterRef.current && !recentCustomerFilterRef.current.contains(event.target as Node)) {
        setRecentCustomerFilterOpen(false);
      }
      if (recentCostCodeFilterRef.current && !recentCostCodeFilterRef.current.contains(event.target as Node)) {
        setRecentCostCodeFilterOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const { data: recentFilterCostCodesData } = useQuery({
    queryKey: ["cost-codes-recent-filter", recentCostCodeFilterSearch],
    queryFn: () => getCostCodes(recentCostCodeFilterSearch),
    enabled: !!user,
  });
  const { data: recentFilterCustomersData } = useQuery({
    queryKey: ["customers-recent-filter", recentCustomerFilterSearch],
    queryFn: () => getCustomers(recentCustomerFilterSearch),
    enabled: !!user,
  });
  const recentEntryParams = React.useMemo(() => {
    const params = new URLSearchParams();
    if (user) params.set("user_id", String(user.id));
    if (recentFilterCostCode !== "all") params.set("cost_code_id", recentFilterCostCode);
    if (recentFilterCustomer !== "all") params.set("customer_id", recentFilterCustomer);
    if (recentFromDate) params.set("from_date", `${recentFromDate}T00:00:00`);
    if (recentToDate) params.set("to_date", `${recentToDate}T23:59:59`);
    if (recentSearch.trim()) params.set("search", recentSearch.trim());
    params.set("limit", String(recentPageSize));
    params.set("offset", String(recentPage * recentPageSize));
    return params;
  }, [user, recentPage, recentPageSize, recentFilterCostCode, recentFilterCustomer, recentFromDate, recentToDate, recentSearch]);

  const { data: recentEntriesData } = useQuery({
    queryKey: ["recent-entries", recentEntryParams.toString()],
    queryFn: () => getEntries(recentEntryParams),
    enabled: !!user,
  });
  const { data: recentCountData } = useQuery({
    queryKey: ["recent-entries-count", user?.id, recentFilterCostCode, recentFilterCustomer, recentFromDate, recentToDate, recentSearch],
    queryFn: () => {
      const params = new URLSearchParams();
      if (user) params.set("user_id", String(user.id));
      if (recentFilterCostCode !== "all") params.set("cost_code_id", recentFilterCostCode);
      if (recentFilterCustomer !== "all") params.set("customer_id", recentFilterCustomer);
      if (recentFromDate) params.set("from_date", `${recentFromDate}T00:00:00`);
      if (recentToDate) params.set("to_date", `${recentToDate}T23:59:59`);
      if (recentSearch.trim()) params.set("search", recentSearch.trim());
      return getEntryCount(params);
    },
    enabled: !!user,
  });
  const updateFieldNotesMutation = useMutation({
    mutationFn: ({ id, field_notes }: { id: number; field_notes: string | null }) => updateEntry(id, { field_notes }),
    onSuccess: async () => {
      setFieldNotesDialogOpen(false);
      setFieldNotesEntry(null);
      setFieldNotesText("");
      await queryClient.invalidateQueries({ queryKey: ["entries"] });
      await queryClient.invalidateQueries({ queryKey: ["recent-entries"] });
    },
  });

  const myRecentEntries = recentEntriesData ?? [];

  async function handlePersonalExport() {
    setExportError(null);
    try {
      const exportParams = new URLSearchParams(recentEntryParams);
      exportParams.delete("offset");
      exportParams.delete("limit");
      const csv = await exportEntries(exportParams);
      downloadText(`my_timesheet_${toISODate(new Date())}.csv`, csv, "text/csv;charset=utf-8");
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 429) {
          setExportError("Rate limited. Please wait a minute before trying to export again.");
        } else {
          setExportError(error.message || "Export failed.");
        }
      } else {
        setExportError("Export failed.");
      }
    }
  }


  return {
    user,
    queryClient,
    isAdmin,
    canEditPunches,
    managedEditUserIds,
    recentCostCodeFilterSearch,
    setRecentCostCodeFilterSearch,
    recentCostCodeFilterOpen,
    setRecentCostCodeFilterOpen,
    recentCostCodeFilterRef,
    recentCustomerFilterSearch,
    setRecentCustomerFilterSearch,
    recentCustomerFilterOpen,
    setRecentCustomerFilterOpen,
    recentCustomerFilterRef,
    recentPage,
    setRecentPage,
    recentPageSize,
    setRecentPageSize,
    recentFilterCostCode,
    setRecentFilterCostCode,
    recentFilterCustomer,
    setRecentFilterCustomer,
    recentFromDate,
    setRecentFromDate,
    recentToDate,
    setRecentToDate,
    recentSearch,
    setRecentSearch,
    recentCompact,
    setRecentCompact,
    fieldNotesDialogOpen,
    setFieldNotesDialogOpen,
    fieldNotesEntry,
    setFieldNotesEntry,
    fieldNotesText,
    setFieldNotesText,
    exportError,
    setExportError,
    recentFilterCostCodesData,
    recentFilterCustomersData,
    recentCountData,
    myRecentEntries,
    updateFieldNotesMutation,
    handlePersonalExport,
  };
}
